import { Box, Typography, useTheme, Button, TextField } from "@mui/material";
import { useState }     from "react";
import moment           from "moment";

import ReceiptLongIcon  from "@mui/icons-material/ReceiptLong";
import FilterListIcon   from "@mui/icons-material/FilterList";
import VisibilityIcon   from "@mui/icons-material/Visibility";

import ReportTable, { RRow, RCell } from "components/Tables/ReportTable";
import FixedSizeDialog   from "components/fixedSizeDialog";
import PayslipViewForm   from "../forms/payslipForms/payslipViewForm";
import { useEmployeePayslipsList } from "api/hooks/humanResource";
import { requester }     from "api/pesabackend";
import { currencyFormatter, withoutSymbolFormatter } from "utilities/currencyFormatter";

const PERIODS = ["THIS MONTH", "CUSTOM"];

export default function EmployeePayslipHistoryTable({ employeeId }) {
  const theme  = useTheme();
  const isDark = theme.palette.mode === "dark";

  const cardBg  = isDark ? "rgba(255,255,255,0.03)" : "#ffffff";
  const accent  = theme.palette.secondary.main;
  const textPri = theme.palette.neutral.dark;
  const textSec = theme.palette.neutral.main;
  const border  = isDark ? "rgba(29,158,117,0.14)" : "rgba(29,158,117,0.16)";

  const { employeePayslipList, isLoadingEmployeePayslipList, setFilterRange } = useEmployeePayslipsList(1, employeeId);

  const [openView,     setOpenView]     = useState(false);
  const [viewData,     setViewData]     = useState({});
  const [showCustom,   setShowCustom]   = useState(false);
  const [activePeriod, setActivePeriod] = useState("THIS MONTH");
  const [startDate,    setStartDate]    = useState(moment().startOf("month").format("YYYY-MM-DD"));
  const [endDate,      setEndDate]      = useState(moment().endOf("month").format("YYYY-MM-DD"));

  const inputSx = {
    "& .MuiInputBase-root": { fontSize: "13px", height: "36px", backgroundColor: cardBg, borderRadius: "8px" },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: border },
    "& .MuiInputLabel-root": { fontSize: "12px" },
  };

  const handleClickView = async (id) => {
    const res = await requester.get(`/human_resource/payslip/get/${id}`);
    if (res.status === 200) { setViewData(res.data.data); setOpenView(true); }
  };

  const columns = [
    { label: "Payroll No."             },
    { label: "Payment Date"            },
    { label: "Renumeration", align: "right" },
    { label: "Allowance",    align: "right" },
    { label: "Deduction",    align: "right" },
    { label: "Net Total",    align: "right" },
    { label: "Created By"              },
    { label: "Actions",      align: "center" },
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "1rem" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Box sx={{ width: "36px", height: "36px", borderRadius: "9px", backgroundColor: "rgba(29,158,117,0.12)", display: "flex", alignItems: "center", justifyContent: "center", color: accent, flexShrink: 0 }}>
            <ReceiptLongIcon sx={{ fontSize: "19px" }} />
          </Box>
          <Box>
            <Typography sx={{ fontFamily: "'DM Serif Display', serif", fontSize: "18px", fontWeight: 400, color: textPri }}>Payslip History</Typography>
            <Typography sx={{ fontSize: "12px", color: textSec }}>{employeePayslipList.length} records</Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "0.75rem", p: "0.75rem 1rem", backgroundColor: cardBg, border: `1px solid ${border}`, borderRadius: "10px" }}>
        <FilterListIcon sx={{ fontSize: "16px", color: accent }} />
        <Box sx={{ display: "flex", gap: "5px" }}>
          {PERIODS.map((p) => (
            <Box key={p} onClick={() => { setActivePeriod(p); if (p === "THIS MONTH") { setStartDate(moment().startOf("month").format("YYYY-MM-DD")); setEndDate(moment().endOf("month").format("YYYY-MM-DD")); setShowCustom(false); } else { setShowCustom(true); } }}
              sx={{ px: "0.65rem", py: "0.25rem", borderRadius: "20px", fontSize: "12px", fontWeight: 500, cursor: "pointer", border: `1px solid ${activePeriod === p ? accent : border}`, color: activePeriod === p ? accent : textSec, backgroundColor: activePeriod === p ? "rgba(29,158,117,0.1)" : "transparent", transition: "all 0.15s" }}>
              {p === "THIS MONTH" ? "This month" : "Custom"}
            </Box>
          ))}
        </Box>
        {showCustom && (
          <>
            <TextField label="Start date" type="date" size="small" value={startDate} onChange={(e) => setStartDate(e.target.value)} inputProps={{ max: endDate }} sx={{ ...inputSx, width: "150px" }} />
            <TextField label="End date" type="date" size="small" value={endDate} onChange={(e) => setEndDate(e.target.value)} inputProps={{ min: startDate }} sx={{ ...inputSx, width: "150px" }} />
          </>
        )}
        <Button onClick={() => setFilterRange({ start: startDate, end: endDate })} sx={{ fontSize: "12px", fontWeight: 500, px: "1rem", py: "0.35rem", borderRadius: "8px", backgroundColor: accent, color: "#fff", textTransform: "none", "&:hover": { backgroundColor: "#0F6E56" } }}>Apply</Button>
      </Box>

      <ReportTable columns={columns} rows={employeePayslipList} loading={isLoadingEmployeePayslipList}
        emptyMessage="No payslips found for this period"
        renderRow={(row, index) => (
          <RRow key={row.id} index={index}>
            <RCell><Typography sx={{ fontSize: "13px", fontWeight: 500, color: textPri }}>{row.id}</Typography></RCell>
            <RCell>{moment(row.payment_date).format("LL")}</RCell>
            <RCell align="right">{withoutSymbolFormatter(parseFloat(row.total_renumeration))}</RCell>
            <RCell align="right">{withoutSymbolFormatter(parseFloat(row.total_allowance))}</RCell>
            <RCell align="right">{withoutSymbolFormatter(parseFloat(row.total_deduction))}</RCell>
            <RCell align="right"><Typography sx={{ fontSize: "13px", fontWeight: 500, color: accent }}>{currencyFormatter(parseFloat(Number(row.total_renumeration) + Number(row.total_allowance) - Number(row.total_deduction)))}</Typography></RCell>
            <RCell>{row.added_by.first_name} {row.added_by.last_name}</RCell>
            <RCell align="center">
              <Box onClick={() => handleClickView(row.id)} sx={{ display: "inline-flex", alignItems: "center", gap: "4px", px: "8px", py: "4px", borderRadius: "6px", border: "1px solid rgba(29,158,117,0.3)", cursor: "pointer", color: accent, fontSize: "12px", fontWeight: 500, "&:hover": { backgroundColor: "rgba(29,158,117,0.08)" } }}>
                <VisibilityIcon sx={{ fontSize: "14px" }} /> View
              </Box>
            </RCell>
          </RRow>
        )}
      />

      <FixedSizeDialog open={openView} handleClose={() => setOpenView(false)} dialogHeading="Payslip Details" negativeResponse="Close" maxWidth="md" scroll="body"
        content={<PayslipViewForm viewData={viewData} />}
      />
    </Box>
  );
}